/**
 * HTTP клиент към API-то.
 *
 * Access токенът живее в паметта, refresh токенът — в httpOnly cookie.
 * При 401 се прави едно подновяване и заявката се повтаря веднъж.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

async function refreshAccessToken(): Promise<boolean> {
  const response = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
  if (!response.ok) return false;

  const body = (await response.json()) as { accessToken: string };
  accessToken = body.accessToken;
  return true;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Multipart заявка — тялото се подава както е. */
  raw?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers = new Headers(options.headers);
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

    let body: BodyInit | undefined;
    if (options.raw) {
      body = options.body as BodyInit;
    } else if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.body);
    }

    return fetch(path, { ...options, headers, body, credentials: 'include' });
  };

  let response = await send();

  if (response.status === 401 && accessToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Грешка ${response.status}`;

    throw new ApiError(message, response.status, payload);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
